import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Phone, Mail, Building2, Copy, Check } from 'lucide-react';
import { DriverDetails } from '@/types/driver.types';
import { useState } from 'react';
import { toast } from 'sonner';

interface DriverContactCardProps {
  driver: DriverDetails;
}

export function DriverContactCard({ driver }: DriverContactCardProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('تم النسخ');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('فشل النسخ');
    }
  };

  const contactItems = [
    {
      icon: Phone,
      label: 'رقم الهاتف',
      value: driver.phone,
      action: `tel:${driver.phone}`,
      actionLabel: 'اتصال',
      copyable: true,
      field: 'phone',
    },
    {
      icon: Mail,
      label: 'البريد الإلكتروني',
      value: driver.email,
      action: `mailto:${driver.email}`,
      actionLabel: 'إرسال',
      copyable: true,
      field: 'email',
    },
    {
      icon: Building2,
      label: 'الناقل',
      value: driver.carrier_name,
      copyable: false,
      field: 'carrier',
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Phone className="h-4 w-4" />
          معلومات التواصل
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {contactItems.map((item) => {
          const Icon = item.icon;
          
          return (
            <div key={item.field} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-sm font-medium truncate" dir="ltr">{item.value || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {item.copyable && item.value && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleCopy(item.value, item.field)}
                  >
                    {copiedField === item.field ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                )}
                {item.action && item.value && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    asChild
                  >
                    <a href={item.action}>{item.actionLabel}</a>
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {/* Driver ID */}
        <div className="pt-3 border-t">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">معرّف السائق</p>
              <p className="text-xs font-mono text-muted-foreground truncate max-w-[200px]" dir="ltr">
                {driver.id}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleCopy(driver.id, 'id')}
            >
              {copiedField === 'id' ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
